import { AxiosResponse } from 'axios'
import { Notify } from '~/lib/notify'
import { Mimetypes } from '~/types/mimetypes'
import mime from 'mime'

/**
 * Downloads a file using an Axios response.
 *
 * @param {AxiosResponse} response - The Axios response containing the file to download.
 * This response should include a 'content-disposition' header with a 'filename' parameter,
 * and its data should be a Blob representing the file content.
 * @param {string} [defaultFilename="default-filename.pdf"]
 */
export function axiosDownloadFile(response: AxiosResponse<Blob>, defaultFilename = 'default-filename') {
  // extract filename
  const blob = response.data as unknown as Blob
  const contentType = String(response.headers['content-type'])
  let filename = tryGetFilenameFromHeaders(response)
  if (!filename) {
    const extension = mime.getExtension(blob.type)
    filename = `${defaultFilename}.${extension}`
  }

  // trigger download of response
  triggerDownloadBlob(blob, filename, contentType)
}

function tryGetFilenameFromHeaders(response: AxiosResponse<Blob>) {
  const headerLine: string | undefined = response.headers['content-disposition']
  if (!headerLine) {
    return undefined
  }
  const startFileNameIndex = headerLine.indexOf('filename=') + 'filename='.length
  if (startFileNameIndex === -1) {
    return undefined
  }
  const raw = headerLine.substring(startFileNameIndex).replaceAll('"', '')
  // The backend percent-encodes the whole name, reserved characters like `,` or `&` included.
  return decodeURIComponent(raw)
}

/**
 * Triggers a file download with given content and filename.
 *
 * @param {BlobPart} content - The content to be downloaded.
 * @param {string} filename - The name of the file to be downloaded.
 * @param type
 */
export function triggerDownloadBlob(content: BlobPart, filename: string, type: Mimetypes = 'text/plain') {
  const blob = new Blob([content], { type })
  const url = URL.createObjectURL(blob)
  triggerDownloadUrl(url, filename)
}

export function triggerDownloadFile(file: File) {
  const url = URL.createObjectURL(file)
  triggerDownloadUrl(url, file.name)
}

export function triggerDownloadUrl(url: string, filename: string) {
  // Create a new anchor element and set its href attribute to the object URL
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.target = '_blank' // important for tests, otherwise we get cors errors

  if (import.meta.dev) {
    Notify.info(`Download ${filename}`)
  }

  // Append the anchor element to the document body and call its click method to start the download
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(link.href)
}

export function openBlobInNewTab(blob: Blob): void {
  // Guard clause for Nuxt SSR (ensures code only runs in the browser)
  if (typeof window === 'undefined') return

  // 1. Create a unique URL representing the blob object
  const blobUrl = URL.createObjectURL(blob)

  // 2. Attempt to open the URL in a new tab
  const newTab = window.open(blobUrl, '_blank')

  // 3. Memory Management / Cleanup
  if (newTab) {
    newTab.onload = () => URL.revokeObjectURL(blobUrl)
  } else {
    console.warn('Popup blocked! Please allow popups for this site.')
    const extension = mime.getExtension(blob.type)
    triggerDownloadUrl(blobUrl, `${blobUrl}.${extension}`)
    URL.revokeObjectURL(blobUrl)
  }
}

export function string2blob(content: string, mimetype: Mimetypes): Blob {
  return new Blob([content], { type: mimetype })
}
export function blob2file(blob: Blob, filename: string): File {
  return new File([blob], filename, { type: blob.type })
}
export function string2file(content: string, filename: string, mimetype: Mimetypes): File {
  return blob2file(string2blob(content, mimetype), filename)
}

/**
 * Converts a HTML <table> to a Spreadsheet file.
 * TODO: Why no CSV?
 */
export async function tableToSpreadsheet(tableRef: HTMLTableElement, filename: `${string}.xlsx`) {
  const xlsx = await import('xlsx')
  const wb = xlsx.utils.table_to_book(tableRef, { sheet: 'Sheet1' })

  // Needed?
  const cellStyle = {
    alignment: {
      wrapText: '1',
    },
  }

  // Replace escaped linebreaks from xlsx with real line-breaks
  for (const key of Object.keys(wb.Sheets.Sheet1).filter((key) => !key.startsWith('!'))) {
    wb.Sheets.Sheet1[key].s ??= {}
    const cell = wb.Sheets.Sheet1[key]
    if (typeof cell.v === 'string' && cell.v.includes('\\n')) {
      cell.v = cell.v.replaceAll('\\n ', '\n')
      cell.v = cell.v.replaceAll('\\n', '\n')
    }

    cell.s ??= {}
    Object.assign(cell.s, cellStyle)
  }

  xlsx.writeFileXLSX(wb, filename)
  // const csv = xlsx.utils.sheet_to_csv(wb.Sheets['Sheet1'])
  // triggerDownloadBlob(csv, filename, 'text/csv;charset=utf-8')
}
